import { useEffect, useRef } from 'react'
import { jwtDecode } from 'jwt-decode'
import { STORAGE_KEYS } from '../../constants/app.constants'
import { hasRefreshToken, refreshAccessToken } from '../../utils/refresh-access-token'

const EXP_SKEW_SECONDS = 60
// Floor between refreshes. A short-TTL or malformed token could otherwise
// report the access token as perpetually "expiring" and drive a tight loop of
// refresh calls — never refresh more often than this.
const MIN_REFRESH_DELAY_MS = 15_000

/** ms until we should refresh (buffer before `exp`), or null if undecodable. */
const msUntilRefresh = (token: string, skew = EXP_SKEW_SECONDS): number | null => {
  try {
    const { exp } = jwtDecode<{ exp?: number }>(token)
    if (!exp) return null
    return Math.max(exp * 1000 - Date.now() - skew * 1000, 0)
  } catch {
    return null
  }
}

/**
 * Keeps the staff session alive while the tab is open: refreshes the access
 * token ~`bufferSeconds` before it expires, so the admin doesn't get bounced
 * to login just for leaving a tab open. Single-flight (shared with the 401
 * retry in api/client.ts and base.service.ts). Reschedules from the token the
 * refresh just returned (not a re-read of localStorage), and stops if a
 * freshly-refreshed token is still within the skew window — both guard
 * against a tight refresh loop. Also re-checks on focus/visibility.
 */
export const useProactiveTokenRefresh = (bufferSeconds = EXP_SKEW_SECONDS): void => {
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  useEffect(() => {
    let cancelled = false
    let stopped = false

    const clear = () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current)
        timerRef.current = null
      }
    }

    const runRefresh = async () => {
      if (cancelled || stopped || !hasRefreshToken()) return
      const newToken = await refreshAccessToken()
      if (cancelled) return
      if (!newToken) {
        // Only give up for good if the refresh token is confirmed dead —
        // refreshAccessToken() clears it on a definitive 401. A transient
        // failure (network blip, 5xx) leaves it in place, so retry after the
        // floor delay instead of permanently disabling the keepalive.
        if (!hasRefreshToken()) {
          stopped = true
          return
        }
        timerRef.current = setTimeout(() => void runRefresh(), MIN_REFRESH_DELAY_MS)
        return
      }
      const delay = msUntilRefresh(newToken, bufferSeconds)
      if (delay === null) {
        stopped = true
        return
      }
      timerRef.current = setTimeout(
        () => void runRefresh(),
        Math.max(delay, MIN_REFRESH_DELAY_MS),
      )
    }

    const schedule = () => {
      clear()
      if (cancelled || stopped) return
      const token = localStorage.getItem(STORAGE_KEYS.AUTH_TOKEN)
      if (!token) {
        if (hasRefreshToken()) void runRefresh()
        return
      }
      const delay = msUntilRefresh(token, bufferSeconds)
      if (delay === null) return
      timerRef.current = setTimeout(
        () => void runRefresh(),
        Math.max(delay, MIN_REFRESH_DELAY_MS),
      )
    }

    const onFocus = () => {
      if (cancelled) return
      if (typeof document !== 'undefined' && document.hidden) return
      const token = localStorage.getItem(STORAGE_KEYS.AUTH_TOKEN)
      if (!token && hasRefreshToken()) {
        stopped = false
        void runRefresh()
      } else {
        schedule()
      }
    }

    schedule()
    window.addEventListener('focus', onFocus)
    document.addEventListener('visibilitychange', onFocus)

    return () => {
      cancelled = true
      clear()
      window.removeEventListener('focus', onFocus)
      document.removeEventListener('visibilitychange', onFocus)
    }
  }, [bufferSeconds])
}
